import { ShortCode } from '@highereducation/wordpress';
import { BentoBox, type GridProps } from '@highereducation/hen-ui';

type BentoBoxShortcodeProps = {
  columns?: '1' | '2' | '3' | '4';
  gap?: string;
  children?: React.ReactNode;
  [key: string]: unknown;
};

/**
 * BentoBoxShortcode - Shortcode wrapper for BentoBox composite component.
 * Use [hen-ui-bento-box-cell] as children for each cell.
 *
 * `gap` uses the same tokens as HEN Grid: xxs, xs, sm, md, lg, xl, 2xl, 3xl (default sm).
 *
 * @example
 * [hen-ui-bento-box columns="3" gap="sm"]
 *   [hen-ui-bento-box-cell col-span="2" variant="dark"]...[/hen-ui-bento-box-cell]
 *   [hen-ui-bento-box-cell]...[/hen-ui-bento-box-cell]
 * [/hen-ui-bento-box]
 */
type GridGap = NonNullable<GridProps['gap']>;

/** Matches {@link Grid} / design tokens — same as BentoBox → Grid gap. */
const VALID_GAPS = new Set<string>([
  'xxs',
  'xs',
  'sm',
  'md',
  'lg',
  'xl',
  '2xl',
  '3xl',
]);

function parseGap(gap: string | undefined, fallback: GridGap = 'sm'): GridGap {
  if (gap && VALID_GAPS.has(gap)) {
    return gap as GridGap;
  }
  return fallback;
}

function parseColumns(columns: string | undefined): 1 | 2 | 3 | 4 {
  if (
    columns === '1' ||
    columns === '2' ||
    columns === '3' ||
    columns === '4'
  ) {
    return parseInt(columns, 10) as 1 | 2 | 3 | 4;
  }
  return 3;
}

export const BentoBoxShortcode: ShortCode<BentoBoxShortcodeProps> = {
  component: ({ columns = '3', gap = 'sm', children }) => {
    const cols = parseColumns(columns);
    const gapVal = parseGap(gap);
    return (
      <BentoBox columns={cols} gap={gapVal}>
        {children}
      </BentoBox>
    );
  },
};

export { BentoBoxCellShortcode } from './BentoBoxCellShortcode';
